import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Plus, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { buchungenAPI, mandantenAPI } from '../lib/api';
import ManualBuchungModal from '../components/ManualBuchungModal';

const STATUS_LABELS = {
  entwurf: 'Entwurf',
  vorgeschlagen: 'Vorgeschlagen',
  gebucht: 'Gebucht',
  exportiert: 'Exportiert',
  storniert: 'Storniert',
};

const STATUS_FILTERS = ['', 'vorgeschlagen', 'gebucht', 'exportiert', 'storniert'];
const QUELLE_FILTERS = [
  { v: '', l: 'Alle Quellen' },
  { v: 'beleg', l: 'Eingang (Beleg)' },
  { v: 'ausgang', l: 'Ausgang' },
  { v: 'manuell', l: 'Manuell' },
];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);
}

function fmtDate(s) {
  if (!s) return '—';
  return format(new Date(s), 'dd.MM.yyyy');
}

export default function Journal() {
  const { slug } = useParams();
  const [mandant, setMandant] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState('');
  const [quelleF, setQuelleF] = useState('');
  const [konto, setKonto] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    mandantenAPI.get(slug).then((r) => setMandant(r.data)).catch(() => {});
  }, [slug]);

  const reload = useCallback(() => {
    setLoading(true);
    return buchungenAPI
      .list(slug, {
        status: statusF || undefined,
        quelle: quelleF || undefined,
        konto: konto || undefined,
        from: from || undefined,
        to: to || undefined,
      })
      .then((r) => setItems(r.data))
      .catch((err) =>
        toast.error(err.response?.data?.detail || 'Buchungen konnten nicht geladen werden')
      )
      .finally(() => setLoading(false));
  }, [slug, statusF, quelleF, konto, from, to]);

  useEffect(() => {
    const t = setTimeout(reload, 200); // debounce on filter changes
    return () => clearTimeout(t);
  }, [reload]);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Buchungs-Journal</h1>
          {mandant && (
            <div className="muted">{mandant.name} · {mandant.skr_variant}</div>
          )}
        </div>
        <div className="header-meta">
          <button className="btn-primary" onClick={() => setModalOpen(true)}>
            <Plus size={14} /> Manuelle Buchung
          </button>
        </div>
      </header>

      <div className="filters" style={{ flexWrap: 'wrap' }}>
        <label>
          Von:
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Bis:
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Status:
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{s ? STATUS_LABELS[s] : 'Alle'}</option>
            ))}
          </select>
        </label>
        <label>
          Quelle:
          <select value={quelleF} onChange={(e) => setQuelleF(e.target.value)}>
            {QUELLE_FILTERS.map((q) => (
              <option key={q.v} value={q.v}>{q.l}</option>
            ))}
          </select>
        </label>
        <label>
          Konto:
          <input
            type="text" value={konto} onChange={(e) => setKonto(e.target.value)}
            placeholder="Soll oder Haben" pattern="\d{3,8}"
          />
        </label>
        <button className="btn-text" onClick={reload} disabled={loading}>
          {loading ? 'Lädt…' : 'Neu laden'}
        </button>
      </div>

      {loading ? (
        <div className="muted">Lädt…</div>
      ) : items.length === 0 ? (
        <div className="card empty">Keine Buchungen für diese Filter.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Quelle</th>
              <th>Soll</th>
              <th>Haben</th>
              <th>USt-Kennz</th>
              <th>Betrag</th>
              <th>Buchungstext</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id}>
                <td>{fmtDate(b.beleg_datum)}</td>
                <td>
                  {b.beleg_id ? (
                    <Link
                      to={`/mandanten/${slug}/belege/${b.beleg_id}`}
                      className="quelle-link"
                      title={`Eingang #${b.beleg_id}${b.belegnummer ? ' · ' + b.belegnummer : ''}`}
                    >
                      <FileText size={12} /> Eingang #{b.beleg_id}
                    </Link>
                  ) : b.ausgangsrechnung_id ? (
                    <span className="quelle-link" title={`Ausgang #${b.ausgangsrechnung_id}${b.belegnummer ? ' · ' + b.belegnummer : ''}`}>
                      <FileText size={12} /> Ausgang #{b.ausgangsrechnung_id}
                    </span>
                  ) : (
                    <span className="muted small">manuell</span>
                  )}
                </td>
                <td className="num">{b.soll_konto}</td>
                <td className="num">{b.haben_konto}</td>
                <td className="num">{b.ust_kennz || '—'}</td>
                <td className="num">{fmtMoney(b.betrag)}</td>
                <td>
                  {b.buchungstext || '—'}
                  {b.partner_name && (
                    <span className="muted small" style={{ marginLeft: 8 }}>· {b.partner_name}</span>
                  )}
                </td>
                <td>
                  <span className={`badge badge-${b.status}`}>
                    {STATUS_LABELS[b.status] || b.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ManualBuchungModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        slug={slug}
        onSaved={reload}
      />
    </div>
  );
}
